import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, TypeaheadChips } from '../../ui';
import { PlusIcon, XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../../ui/Dialog';
import { Input } from '../../ui/Input';
import { settingsAPI } from '../../../services/api';
import { CREW_COLORS } from '../CrewsPage';
import { useDealerWorkspace } from '../../admin/DealerWorkspaceContext';
import { isDeactivatedStatus, deactivatedUserIds, storedIdsWithUserNames, deactivatedChipOptions } from '../../../utils/userStatus';

/**
 * CrewForm — Settings-shape create/edit form for `list_type='crews'` records.
 * Extracted from `CrewsPage.js` per PT-22 W3-fp1 F1a so InlineCreate's modal
 * can produce Settings-equivalent records (color, description, members,
 * specialties, pending_invites, active) instead of name-only stubs.
 *
 * Props:
 *   value       object|null — existing record for edit mode; null/undefined = create.
 *   onSave(payload) fn      — called with the full crew payload; may throw
 *                             to surface a save error inline.
 *   onCancel()   fn         — dismiss.
 *   autoFocus   bool         — whether to autofocus the name input (default true).
 *
 * The form owns all its state internally, renders its own footer buttons,
 * and pulls the tenant users list itself on mount. Both `CrewsPage` (inside
 * its Slideover — pass `null` footer to Slideover) and `InlineCreate` (inside
 * a Modal — same treatment) render this form the same way.
 *
 * Iter18 §2.4 field order preserved: color·name (inline) → description →
 * palette (6 + expand → 20) → members type-ahead + Invite Techs → specialties
 * → active toggle.
 */

// 20-preset closed palette per SET-3 / OB-6 wires. Hosted in CrewsPage.js so
// the linter's R4 allowlist entry stays authoritative for the DATA constant.
const INITIAL_PALETTE_COUNT = 6;

export const EMPTY_CREW = {
  name: '',
  description: '',
  color: CREW_COLORS[0],
  members: [],
  specialties: [],
  pending_invites: [],
  placeholders: [],
  active: true,
};

export default function CrewForm({ value, onSave, onCancel, autoFocus = true }) {
  const initial = useMemo(() => ({
    ...EMPTY_CREW,
    ...(value || {}),
    members: (value && value.members) || [],
    specialties: (value && value.specialties) || [],
    pending_invites: (value && value.pending_invites) || [],
    placeholders: (value && value.placeholders) || [],
    active: value ? value.active !== false : true,
    color: (value && value.color) || CREW_COLORS[0],
  }), [value]);

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  // IDI-704: raw list_users rows, to label a kept deactivated member.
  const [rawUsers, setRawUsers] = useState([]);
  const [paletteExpanded, setPaletteExpanded] = useState(() => {
    const idx = CREW_COLORS.indexOf(initial.color);
    return idx >= INITIAL_PALETTE_COUNT;
  });
  const [inviteOpen, setInviteOpen] = useState(false);
  // IDI-63: in a super-admin's dealer workspace the pool is that dealer's
  // logins only (scope=tenant), not every login a super-admin can list.
  const workspace = useDealerWorkspace();

  // Load tenant users for the members type-ahead. Self-contained: the form
  // pulls its own pool so it drops in anywhere.
  useEffect(() => {
    let ignore = false;
    const load = async () => {
      try {
        const res = workspace && typeof settingsAPI.getTenantUsers === 'function'
          ? await settingsAPI.getTenantUsers()
          : typeof settingsAPI.getUsers === 'function'
          ? await settingsAPI.getUsers()
          : await settingsAPI.getList('users');
        const list = Array.isArray(res) ? res : res?.items || [];
        if (ignore) return;
        // IDI-704 (IDI-681 D1): a deactivated login is not offered.
        setRawUsers(list);
        setUsers(list
          .filter((u) => u && !isDeactivatedStatus(u.status))
          .map((u) => ({ value: u.id || u.userId || u.sub || u.email, label: u.name || u.email || u.id || u.userId })));
      } catch {
        /* empty pool is fine */
      }
    };
    load();
    return () => { ignore = true; };
  }, [workspace]);

  // IDI-704 (IDI-681 D2): a deactivated person already on the crew keeps a
  // chip-only, iconed option labelled by the current list_users name.
  // form.members is never filtered, so Save writes the stored ids unchanged.
  const memberOptions = useMemo(() => {
    const keyed = rawUsers
      .filter(Boolean)
      .map((u) => ({ ...u, id: u.id || u.userId || u.sub || u.email }));
    const kept = deactivatedChipOptions(
      storedIdsWithUserNames(initial.members, keyed),
      users,
      deactivatedUserIds(keyed),
    );
    return [...users, ...kept];
  }, [users, rawUsers, initial.members]);

  const visibleSwatches = useMemo(
    () => (paletteExpanded ? CREW_COLORS : CREW_COLORS.slice(0, INITIAL_PALETTE_COUNT)),
    [paletteExpanded]
  );

  // R20-31: rows may be either invites (with email) or placeholders (name only).
  // A row's `placeholder` flag decides which array it lands in.
  const acceptInvites = useCallback((rows) => {
    const placeholderRows = rows
      .filter((r) => r.placeholder && r.name && r.name.trim())
      .map((r) => ({ name: r.name.trim() }));
    const inviteRows = rows.filter((r) => !r.placeholder && r.email && r.email.trim());

    if (placeholderRows.length === 0 && inviteRows.length === 0) {
      setInviteOpen(false);
      return;
    }
    setForm((f) => ({
      ...f,
      pending_invites: [...(f.pending_invites || []), ...inviteRows],
      placeholders: [...(f.placeholders || []), ...placeholderRows],
    }));
    setInviteOpen(false);
  }, []);

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await onSave(form);
    } catch (err) {
      setError(err?.message || 'Failed to save crew');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div
          className="px-3 py-2 text-sm"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--color-error) 8%, transparent)',
            color: 'var(--color-error)',
            borderRadius: 'var(--radius-sm)',
          }}
          role="alert"
        >
          {error}
        </div>
      )}

      {/* 1. Color swatch (inline) + Name */}
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>
          Crew Name <span style={{ color: 'var(--color-error)' }}>*</span>
        </label>
        <div className="flex items-center gap-2">
          <span
            className="h-7 w-7 rounded-full flex-shrink-0 border"
            style={{
              backgroundColor: form.color,
              borderColor: 'var(--color-border)',
            }}
            aria-label={`Selected color ${form.color}`}
          />
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Finish Crew"
            className="bidit-input flex-1"
            autoFocus={autoFocus}
          />
        </div>
      </div>

      {/* 2. Description */}
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>
          Description <span className="text-xs font-normal" style={{ color: 'var(--color-text-muted)' }}>(optional)</span>
        </label>
        <input
          type="text"
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          placeholder="e.g. Two-truck spray team — south region"
          className="bidit-input"
        />
      </div>

      {/* 3. Color palette (6 shown + expand → 20) */}
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>
          Crew Color
        </label>
        <div className="flex flex-wrap gap-2">
          {visibleSwatches.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setForm((f) => ({ ...f, color: c }))}
              className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{
                backgroundColor: c,
                borderColor: form.color === c ? 'var(--color-text-default)' : 'transparent',
                transform: form.color === c ? 'scale(1.15)' : undefined,
              }}
              aria-label={`Color ${c}`}
              aria-pressed={form.color === c}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => setPaletteExpanded((v) => !v)}
          className="mt-2 inline-flex items-center gap-1 text-xs font-medium"
          style={{ color: 'var(--color-text-muted)' }}
        >
          {paletteExpanded ? (
            <>
              <ChevronUpIcon className="h-3.5 w-3.5" />
              Fewer colors
            </>
          ) : (
            <>
              <ChevronDownIcon className="h-3.5 w-3.5" />
              More colors
            </>
          )}
        </button>
      </div>

      {/* 4. Members type-ahead + adjacent Invite Techs CTA */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium" style={{ color: 'var(--color-text-default)' }}>
            Crew Members
          </label>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setInviteOpen(true)}
            type="button"
          >
            <PlusIcon className="h-3.5 w-3.5 mr-1" />
            Add techs
          </Button>
        </div>
        <TypeaheadChips
          value={form.members}
          onChange={(members) => setForm((f) => ({ ...f, members }))}
          options={memberOptions}
          placeholder="Search team members…"
          aria-label="Members"
        />
        {/* R20-31: Pending invites — OUTLINED red chip (in flight, heading to active). */}
        {(form.pending_invites || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {form.pending_invites.map((inv, i) => (
              <span
                key={`${inv.email}-${i}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs"
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--color-action-primary)',
                  border: '1px solid var(--color-action-primary)',
                  borderRadius: 'var(--radius-sm)',
                }}
                title={inv.email ? `${inv.email} — pending invite` : 'pending invite'}
              >
                {inv.name || inv.email} — pending
                <button
                  type="button"
                  onClick={() => setForm((f) => ({
                    ...f,
                    pending_invites: f.pending_invites.filter((_, j) => j !== i),
                  }))}
                  aria-label={`Remove invite for ${inv.email}`}
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        {/* R20-31: Placeholders — SOLID grey chip (never had, never will have an account). */}
        {(form.placeholders || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {form.placeholders.map((ph, i) => (
              <span
                key={`${ph.name}-${i}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs"
                style={{
                  backgroundColor: 'var(--neutral-200)',
                  color: 'var(--color-text-default)',
                  borderRadius: 'var(--radius-sm)',
                }}
                title={`${ph.name} — placeholder (no account)`}
              >
                {ph.name}
                <button
                  type="button"
                  onClick={() => setForm((f) => ({
                    ...f,
                    placeholders: f.placeholders.filter((_, j) => j !== i),
                  }))}
                  aria-label={`Remove placeholder ${ph.name}`}
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* 5. Specialties (crew-wide chips) */}
      <SpecialtiesField
        value={form.specialties}
        onChange={(specialties) => setForm((f) => ({ ...f, specialties }))}
      />

      {/* 6. Active toggle */}
      <div
        className="flex items-center gap-3 pt-2 border-t"
        style={{ borderColor: 'var(--color-border)' }}
      >
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
            className="sr-only peer"
            style={{ accentColor: 'var(--color-action-primary)' }}
          />
          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[color:var(--color-action-primary)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[color:var(--color-action-primary)]" style={{ }} />
        </label>
        <span className="text-sm font-medium" style={{ color: 'var(--color-text-default)' }}>
          Active
        </span>
      </div>

      {/* Footer buttons — form-scoped so the same form drops into a Slideover
          (footer=null) or a Modal (DialogBody-hosted). */}
      <div
        className="flex justify-end gap-3 pt-4 border-t"
        style={{ borderColor: 'var(--color-border)' }}
      >
        <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button type="submit" loading={saving} disabled={!form.name.trim() || saving}>
          Save Crew
        </Button>
      </div>

      <InviteTechsModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSubmit={acceptInvites}
      />
    </form>
  );
}

// Specialties field with local option list + inline-create (Iter18 SET-3 / OB-6).
const BASE_SPECIALTIES = [
  { value: 'spray', label: 'Spray' },
  { value: 'blown', label: 'Blown' },
  { value: 'batt', label: 'Batt' },
  { value: 'prep', label: 'Prep' },
  { value: 'finish', label: 'Finish' },
];

function SpecialtiesField({ value, onChange }) {
  const [customOptions, setCustomOptions] = useState([]);
  const allOptions = useMemo(() => {
    const known = new Set([...BASE_SPECIALTIES, ...customOptions].map((o) => o.value));
    const missing = (value || [])
      .filter((v) => !known.has(v))
      .map((v) => ({ value: v, label: v }));
    return [...BASE_SPECIALTIES, ...customOptions, ...missing];
  }, [customOptions, value]);

  return (
    <div>
      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>
        Specialties
      </label>
      <TypeaheadChips
        value={value}
        onChange={onChange}
        options={allOptions}
        placeholder="+ Add specialty"
        allowCreate
        onCreate={(label) => {
          const v = label.trim().toLowerCase();
          if (!v) return;
          setCustomOptions((opts) =>
            opts.some((o) => o.value === v) ? opts : [...opts, { value: v, label: v }]
          );
          onChange?.([...(value || []), v]);
        }}
      />
    </div>
  );
}

// R20-31: modal supports both invites (with email) and placeholders (name only).
// Per-row "Don't send invite" checkbox routes to placeholders on submit.
function InviteTechsModal({ open, onClose, onSubmit }) {
  const emptyRow = () => ({ name: '', email: '', placeholder: false });
  const [rows, setRows] = useState([emptyRow(), emptyRow()]);
  useEffect(() => {
    if (open) setRows([emptyRow(), emptyRow()]);
  }, [open]);

  const updateRow = (idx, field, value) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const addRow = () => setRows((rs) => [...rs, emptyRow()]);

  const hasPlaceholder = rows.some((r) => r.placeholder);
  const hasInvite = rows.some((r) => !r.placeholder && r.email && r.email.trim());
  const submitLabel = hasPlaceholder && hasInvite ? 'Add techs'
                     : hasPlaceholder ? 'Add placeholders'
                     : 'Send invites';

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader onClose={onClose}>Add techs</DialogHeader>
      <DialogBody>
        <p className="text-sm mb-3" style={{ color: 'var(--color-text-muted)' }}>
          Add field techs to this crew. Invited techs will receive an email to set up a free Viewer seat. Placeholders don't get invited &mdash; use them for techs you want to schedule but not give BIDIT access.
        </p>
        <div className="space-y-2">
          {rows.map((row, idx) => (
            <div key={idx} className="space-y-1">
              <div
                className="grid gap-2"
                style={{ gridTemplateColumns: '1.4fr 1.6fr' }}
              >
                <Input
                  placeholder="Name"
                  value={row.name}
                  onChange={(e) => updateRow(idx, 'name', e.target.value)}
                  aria-label={`Row ${idx + 1} name`}
                />
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={row.email}
                  onChange={(e) => updateRow(idx, 'email', e.target.value)}
                  aria-label={`Row ${idx + 1} email`}
                  disabled={row.placeholder}
                />
              </div>
              <label className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                <input
                  type="checkbox"
                  checked={!!row.placeholder}
                  onChange={(e) => updateRow(idx, 'placeholder', e.target.checked)}
                  aria-label={`Row ${idx + 1} don't send invite (placeholder)`}
                  style={{ accentColor: 'var(--color-action-primary)' }}
                />
                Don&apos;t send invite (placeholder)
              </label>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addRow}
          className="mt-3 text-sm font-medium inline-flex items-center gap-1"
          style={{ color: 'var(--color-action-primary)' }}
        >
          <PlusIcon className="h-3.5 w-3.5" />
          Add another
        </button>
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSubmit(rows)}>{submitLabel}</Button>
      </DialogFooter>
    </Dialog>
  );
}
